import React, { useEffect, useMemo, useRef, useState } from "react";

type TeacherKey = "R" | "H" | "M" | "A";

interface CourseLink {
  t: string;
  u: string;
}

interface Teacher {
  name: string;
  archetype: string;
  body: string;
  fixed: CourseLink[];
  pool: CourseLink[];
  browseHref: string;
  browseLabel: string;
}

interface Answer {
  t: string;
  s: TeacherKey;
}

interface Question {
  q: string;
  a: Answer[];
}

interface Props {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const teachers: Record<TeacherKey, Teacher> = {
  R: {
    name: "Rory Sutherland",
    archetype: "The Lateral Thinker's Lateral Thinker",
    body: "You spot the obvious thing everyone else missed because you weren't looking at the obvious thing in the first place. Your mind is a magpie's nest of half-remembered psychology papers, eccentric advertising stories, and the lingering suspicion that most of business is solving the wrong problems beautifully.",
    fixed: [
      { t: "Behavioural Economics", u: "/courses/behavioural-economics" },
      { t: "Applied Behavioural Science", u: "/courses/behavioural-science" },
    ],
    pool: [
      {
        t: "Behavioural Science for Effective Messaging",
        u: "/courses/behavioural-science-for-effective-messaging",
      },
      { t: "Storytelling", u: "/courses/storytelling" },
      { t: "Advertising Masters", u: "/courses/advertising-101" },
      { t: "Digital Marketing", u: "/courses/digital-marketing" },
      { t: "Creative Thinking", u: "/courses/how-to-be-more-creative" },
      { t: "The Best of Nudgestock", u: "/courses/nudgestock" },
    ],
    browseHref: "/courses#behavioural-science",
    browseLabel: "Rory appears on many more courses. Wander through the lot →",
  },
  H: {
    name: "Sir John Hegarty",
    archetype: "The Creative Maestro",
    body: "You believe ideas are everything, and you're convinced most companies are quietly suffocating theirs under a meeting agenda. You can tell within forty seconds whether a piece of work was made by someone who cared. You probably also have strong opinions about typography.",
    fixed: [
      { t: "Creative Leadership", u: "/courses/creative-leadership" },
      { t: "Creative Thinking", u: "/courses/how-to-be-more-creative" },
      { t: "Advertising Masters", u: "/courses/advertising-101" },
    ],
    pool: [],
    browseHref: "/courses#marketing-strategy",
    browseLabel: "",
  },
  M: {
    name: "Meik Wiking",
    archetype: "The Reluctant Optimist",
    body: "You quietly suspect that most of business's biggest problems would dissolve overnight if everyone got slightly more sleep and a slightly better lunch. You're often right. You read happiness research so you can quote it at people who refuse to take a holiday.",
    fixed: [
      { t: "The Happy Course", u: "/courses/happiness" },
      {
        t: "The Even Happier Course",
        u: "/courses/the-even-happier-course",
      },
    ],
    pool: [],
    browseHref: "/courses#leadership-wellbeing",
    browseLabel: "",
  },
  A: {
    name: "Dan Ariely",
    archetype: "The Behavioural Detective",
    body: "You are deeply suspicious of intuition. You think most decisions are made by accident and then rationalised afterwards with terrifying confidence. You collect cognitive biases the way other people collect wine, and you're always slightly delighted to spot one in the wild, especially your own.",
    fixed: [
      {
        t: "Behavioural Science for Life",
        u: "/courses/behavioural-science-for-life",
      },
      { t: "Behavioural Economics", u: "/courses/behavioural-economics" },
    ],
    pool: [],
    browseHref: "/courses#behavioural-science",
    browseLabel: "",
  },
};

const questions: Question[] = [
  {
    q: "It's 11pm on a Sunday. What are you doing?",
    a: [
      {
        t: "Mainlining a pricing-psychology paper because someone on Twitter swore it would change your life.",
        s: "R",
      },
      {
        t: "Sketching something you can't quite explain. It's for a brief that comes later.",
        s: "H",
      },
      {
        t: "Lighting a candle and quietly pretending Monday doesn't exist.",
        s: "M",
      },
      {
        t: "Re-reading the same experiment, convinced the data is hiding something.",
        s: "A",
      },
    ],
  },
  {
    q: 'A colleague announces: "Everyone agrees this is the best approach." You say...',
    a: [
      { t: '"What if everyone is wrong? Hear me out."', s: "R" },
      { t: '"Best is the enemy of brilliant. Let\'s try again."', s: "H" },
      { t: "\"I'm not sure 'everyone' has had any sleep.\"", s: "M" },
      {
        t: '"What does the data actually say? Not what you remember it saying."',
        s: "A",
      },
    ],
  },
  {
    q: "Pick a perfectly good way to spend a Tuesday evening.",
    a: [
      {
        t: "A walk during which you have approximately seven excellent ideas.",
        s: "R",
      },
      {
        t: "A long browse of an art bookshop you didn't plan to enter.",
        s: "H",
      },
      { t: "Cooking properly. Not for performance. For pleasure.", s: "M" },
      {
        t: "A long supper with the friend who happily disagrees with you on absolutely everything. Sworn enemies on paper, best company on earth.",
        s: "A",
      },
    ],
  },
  {
    q: "Your secret professional vice is...",
    a: [
      {
        t: "Reading academic papers for fun and then mangling them at dinner parties.",
        s: "R",
      },
      {
        t: "Defending a brilliantly mad idea in a room of nine sensible people. Loudly.",
        s: "H",
      },
      {
        t: "Closing your laptop at 5:30pm. You don't apologise for this.",
        s: "M",
      },
      {
        t: "Spotting yourself in your own bias, and finding it more delightful than embarrassing.",
        s: "A",
      },
    ],
  },
  {
    q: "If you had to give a talk tomorrow, it would be titled...",
    a: [
      {
        t: '"Everything you believe about [topic] is probably backwards."',
        s: "R",
      },
      { t: '"Be brave. Or be invisible. There is no third option."', s: "H" },
      { t: '"The case for doing slightly less, on purpose."', s: "M" },
      { t: '"The three biases quietly running your company."', s: "A" },
    ],
  },
];

const initialScores = (): Record<TeacherKey, number> => ({
  R: 0,
  H: 0,
  M: 0,
  A: 0,
});

const winnerFromScores = (scores: Record<TeacherKey, number>): TeacherKey => {
  return (["R", "H", "M", "A"] as TeacherKey[]).reduce((winner, key) => {
    return scores[key] > scores[winner] ? key : winner;
  }, "R");
};

const coursesForTeacher = (teacher: Teacher): CourseLink[] => {
  const displayed = teacher.fixed.slice();

  if (teacher.pool.length > 0) {
    displayed.push(
      teacher.pool[Math.floor(Math.random() * teacher.pool.length)],
    );
  }

  return displayed;
};

const SpiritTeacherQuiz: React.FC<Props> = ({ isOpen, onOpen, onClose }) => {
  const [step, setStep] = useState(-1);
  const [scores, setScores] = useState(initialScores);
  const [resultKey, setResultKey] = useState<TeacherKey | null>(null);
  const [resultCourses, setResultCourses] = useState<CourseLink[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstAnswerRef = useRef<HTMLButtonElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  const currentQuestion = step >= 0 ? questions[step] : null;
  const resultTeacher = resultKey ? teachers[resultKey] : null;
  const primaryCourse = resultCourses[0];

  const titleId = useMemo(() => {
    if (resultTeacher) {
      return "quiz-result-title";
    }

    return currentQuestion ? "quiz-question-title" : "quiz-intro-title";
  }, [currentQuestion, resultTeacher]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isOpen) {
        if (event.key === "Escape") {
          onClose();
        }

        return;
      }

      const target = event.target as HTMLElement;
      const tagName = target.tagName ? target.tagName.toLowerCase() : "";

      if (
        tagName === "input" ||
        tagName === "textarea" ||
        target.isContentEditable
      ) {
        return;
      }

      if (event.key === "?") {
        event.preventDefault();
        onOpen();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, onOpen]);

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove("quiz-open");
      lastFocusRef.current?.focus();
      return;
    }

    lastFocusRef.current = document.activeElement as HTMLElement;
    document.body.classList.add("quiz-open");
    window.setTimeout(() => modalRef.current?.focus(), 30);

    return () => document.body.classList.remove("quiz-open");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentQuestion) {
      modalRef.current?.scrollTo(0, 0);
      firstAnswerRef.current?.focus();
    }
  }, [currentQuestion, isOpen]);

  const startQuiz = () => {
    setScores(initialScores());
    setResultKey(null);
    setResultCourses([]);
    setStep(0);
  };

  const answerQuestion = (score: TeacherKey) => {
    const nextScores = {
      ...scores,
      [score]: scores[score] + 1,
    };
    const nextStep = step + 1;

    setScores(nextScores);

    if (nextStep >= questions.length) {
      const winner = winnerFromScores(nextScores);
      setResultKey(winner);
      setResultCourses(coursesForTeacher(teachers[winner]));
      setStep(questions.length);
      modalRef.current?.scrollTo(0, 0);
      return;
    }

    setStep(nextStep);
  };

  return (
    <div
      className={`quiz-overlay${isOpen ? " is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-hidden={!isOpen}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="quiz-modal" tabIndex={-1} ref={modalRef}>
        <button
          type="button"
          className="quiz-close"
          aria-label="Close quiz"
          onClick={onClose}
        >
          &times;
        </button>

        {!currentQuestion && !resultTeacher && (
          <>
            <span className="quiz-eyebrow">A 42courses Easter Egg</span>
            <h2 className="quiz-title" id={titleId}>
              Which 42courses brain is <em>yours</em>?
            </h2>
            <p className="quiz-intro">
              A short diagnostic in the Cosmo tradition. Five questions, four
              possible verdicts, one teacher to match. No biology required.
            </p>
            <button
              type="button"
              className="quiz-start-btn"
              onClick={startQuiz}
            >
              Begin the diagnosis →
            </button>
          </>
        )}

        {currentQuestion && !resultTeacher && (
          <>
            <span className="quiz-eyebrow">
              Question {step + 1} of {questions.length}
            </span>
            <div className="quiz-progress" aria-hidden="true">
              {questions.map((_, index) => (
                <span
                  key={index}
                  className={index < step ? "done" : undefined}
                />
              ))}
            </div>
            <h2 className="quiz-question" id={titleId}>
              {currentQuestion.q}
            </h2>
            <div className="quiz-answers" role="list">
              {currentQuestion.a.map((answer, index) => (
                <button
                  type="button"
                  className="quiz-answer"
                  role="listitem"
                  key={answer.t}
                  ref={index === 0 ? firstAnswerRef : undefined}
                  onClick={() => answerQuestion(answer.s)}
                >
                  {answer.t}
                </button>
              ))}
            </div>
          </>
        )}

        {resultTeacher && primaryCourse && (
          <>
            <span className="quiz-eyebrow">Your verdict</span>
            <h2 className="quiz-result-name" id={titleId}>
              {resultTeacher.name}
            </h2>
            <p className="quiz-result-archetype">{resultTeacher.archetype}</p>
            <p className="quiz-result-body">{resultTeacher.body}</p>
            <div className="quiz-result-courses">
              <strong>Where to start</strong>
              {resultCourses.map((course) => (
                <a className="quiz-course-link" href={course.u} key={course.u}>
                  {course.t} <span aria-hidden="true">→</span>
                </a>
              ))}
              {resultTeacher.browseLabel && (
                <a className="quiz-browse-more" href={resultTeacher.browseHref}>
                  {resultTeacher.browseLabel}
                </a>
              )}
            </div>
            <div className="quiz-result-actions">
              <a href={primaryCourse.u} className="btn-primary">
                Start with {primaryCourse.t} →
              </a>
              <button
                type="button"
                className="btn-secondary"
                onClick={startQuiz}
              >
                Take it again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpiritTeacherQuiz;
